import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr, fmtTime, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, SectionCard } from "@/components/Bits";
import { EmpAvatar } from "@/components/EmpAvatar";
import { UploadButton } from "@/components/Upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LogIn, LogOut, Coffee, Undo2, Pencil, Plus, Trash2, Paperclip, FileText, CalendarClock, Wifi, MapPin } from "lucide-react";
import { toast } from "sonner";

const TZ = "Asia/Singapore";

// Best-effort capture of the device's network hint + GPS location at clock time.
// The WiFi SSID is NOT accessible to web apps; we capture connection type + coords instead.
const collectClockContext = () => new Promise((resolve) => {
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const connection_type = c ? (c.type || c.effectiveType || null) : null;
  if (!navigator.geolocation) { resolve({ connection_type }); return; }
  let done = false;
  const finish = (extra) => { if (!done) { done = true; resolve({ connection_type, ...extra }); } };
  navigator.geolocation.getCurrentPosition(
    (pos) => finish({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
    () => finish({}),
    { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
  );
  setTimeout(() => finish({}), 8500);
});

function ClockMeta({ meta }) {
  if (!meta) return <span className="text-xs text-muted-foreground">—</span>;
  const badges = [];
  if (meta.on_network === true || meta.on_network === false)
    badges.push(<span key="n" className={`inline-flex items-center gap-1 ${meta.on_network ? "text-success" : "text-warning"}`}><Wifi className="h-3 w-3" />{meta.on_network ? "Office net" : "Off-net"}</span>);
  if (meta.on_site === true || meta.on_site === false)
    badges.push(<span key="s" className={`inline-flex items-center gap-1 ${meta.on_site ? "text-success" : "text-warning"}`}><MapPin className="h-3 w-3" />{meta.on_site ? "On-site" : "Off-site"}</span>);
  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-[11px]" title={`IP: ${meta.ip || "—"}${meta.connection_type ? " · " + meta.connection_type : ""}${meta.geo ? ` · ±${Math.round(meta.geo.accuracy || 0)}m` : ""}`} data-testid="clock-meta">
      {badges.length ? badges : <span className="text-muted-foreground">{meta.connection_type || meta.ip || "—"}</span>}
    </span>
  );
}

const toSgtInput = (iso) => {
  if (!iso) return "";
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(new Date(iso));
  const g = (t) => (p.find((x) => x.type === t) || {}).value;
  return `${g("year")}-${g("month")}-${g("day")}T${g("hour")}:${g("minute")}`;
};

export default function Attendance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isMgr = user?.role === "admin" || user?.role === "manager";
  const [today, setToday] = useState({});
  const [digest, setDigest] = useState({ entries: [] });
  const [period, setPeriod] = useState("month");
  const [list, setList] = useState({ records: [], total_work_hours: 0, total_access_hours: 0 });
  const [now, setNow] = useState(new Date());
  const [clockOut, setClockOut] = useState(false);
  const [editRec, setEditRec] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const curMonth = new Date().toLocaleDateString("en-CA", { timeZone: TZ }).slice(0, 7);
  useEffect(() => { api.get(`/public-holidays?year=${curMonth.slice(0, 4)}`).then((r) => setHolidays(r.data)).catch(() => {}); }, [curMonth]);
  const monthHolidays = holidays.filter((h) => h.date.startsWith(curMonth));

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const loadToday = useCallback(() => api.get("/attendance/today").then((r) => setToday(r.data)).catch(() => {}), []);
  const loadList = useCallback(() => api.get(`/attendance?period=${period}`).then((r) => setList(r.data)).catch(() => {}), [period]);

  useEffect(() => { loadToday(); }, [loadToday]);
  useEffect(() => { loadList(); }, [loadList]);
  const loadDigest = useCallback(() => { if (isMgr) api.get("/attendance/work-log-digest").then((r) => setDigest(r.data)).catch(() => {}); }, [isMgr]);
  useEffect(() => { loadDigest(); }, [loadDigest]);

  const act = async (action) => {
    try {
      const ctx = await collectClockContext();
      await api.post(`/attendance/${action}`, ctx);
      toast.success(`Recorded ${action.replace(/-/g, " ")}`);
      loadToday();
      loadList();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const steps = [
    { key: "clock-in", field: "clock_in", label: "Clock In", icon: LogIn, color: "bg-primary text-primary-foreground" },
    { key: "break1-start", field: "break1_start", label: "Break 1 Out", icon: Coffee, color: "bg-warning text-warning-foreground" },
    { key: "break1-end", field: "break1_end", label: "Break 1 In", icon: Undo2, color: "bg-warning text-warning-foreground" },
    { key: "break2-start", field: "break2_start", label: "Break 2 Out", icon: Coffee, color: "bg-warning text-warning-foreground" },
    { key: "break2-end", field: "break2_end", label: "Break 2 In", icon: Undo2, color: "bg-warning text-warning-foreground" },
    { key: "clock-out", field: "clock_out", label: "Clock Out", icon: LogOut, color: "bg-destructive text-destructive-foreground" },
  ];

  const disabled = (key) => {
    if (today[steps.find((s) => s.key === key).field]) return true;
    const has = (f) => !!today[f];
    switch (key) {
      case "clock-in": return false;
      case "break1-start": return !has("clock_in");
      case "break1-end": return !has("break1_start");
      case "break2-start": return !has("clock_in");
      case "break2-end": return !has("break2_start");
      case "clock-out": return !has("clock_in");
      default: return false;
    }
  };

  const onStep = (key) => { if (key === "clock-out") setClockOut(true); else act(key); };

  return (
    <div className="animate-fade-up">
      <PageTitle overline="Time & Attendance · SGT" title="Attendance" />

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-1 p-6 rounded-md border-border flex flex-col items-center justify-center text-center">
          <p className="overline">Singapore Time</p>
          <p className="font-heading font-extrabold text-5xl font-mono-num mt-2 tracking-tight">
            {now.toLocaleTimeString("en-SG", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: false })}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {now.toLocaleDateString("en-SG", { timeZone: TZ, weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
          </p>
          <div className="mt-5 px-4 py-2 rounded-md bg-muted border border-border">
            <span className="text-sm text-muted-foreground">Worked today: </span>
            <span className="font-mono-num font-bold">{(today.work_hours || 0).toFixed(2)}h</span>
            {today.access_hours > 0 && <span className="text-xs text-success ml-2">+{today.access_hours}h OIL</span>}
          </div>
          {today.clock_in_meta && <div className="mt-2 text-center" data-testid="today-clock-meta"><ClockMeta meta={today.clock_in_meta} /></div>}
        </Card>

        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-4">
          {steps.map((s) => (
            <button
              key={s.key}
              onClick={() => onStep(s.key)}
              disabled={disabled(s.key)}
              data-testid={`${s.key}-button`}
              className={`group relative flex flex-col items-start justify-between p-5 rounded-md border border-border transition-transform duration-200 disabled:opacity-40 disabled:cursor-not-allowed enabled:hover:-translate-y-1 enabled:hover:shadow-md ${today[s.field] ? "bg-muted" : "bg-card"}`}
            >
              <div className={`h-10 w-10 rounded-md flex items-center justify-center ${today[s.field] ? "bg-muted-foreground/10 text-muted-foreground" : s.color}`}>
                <s.icon className="h-5 w-5" />
              </div>
              <div className="mt-4 text-left">
                <p className="font-heading font-bold">{s.label}</p>
                <p className="font-mono-num text-sm text-muted-foreground mt-0.5">{today[s.field] ? fmtTime(today[s.field]) : "Not recorded"}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {monthHolidays.length > 0 && (
        <div className="mt-5 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3" data-testid="attendance-holidays">
          <p className="overline text-destructive mb-2 inline-flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Public Holidays this month</p>
          <div className="flex flex-wrap gap-2">
            {monthHolidays.map((h) => (
              <span key={h.id} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-destructive/10 text-destructive font-medium" data-testid={`att-holiday-${h.date}`}>
                {fmtDate(h.date)} · {h.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <SectionCard
        title="Attendance History"
        action={
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList>
              <TabsTrigger value="week" data-testid="period-week">Week</TabsTrigger>
              <TabsTrigger value="month" data-testid="period-month">Month</TabsTrigger>
            </TabsList>
          </Tabs>
        }
      >
        <div className="flex gap-6 mb-4 text-sm">
          <div><span className="overline">Total Work</span><p className="font-mono-num font-bold text-lg">{list.total_work_hours}h</p></div>
          <div><span className="overline">Access / OIL</span><p className="font-mono-num font-bold text-lg text-success">{list.total_access_hours}h</p></div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Clock In</TableHead>
                <TableHead>Break 1</TableHead>
                <TableHead>Break 2</TableHead>
                <TableHead>Clock Out</TableHead>
                <TableHead>Network / Location</TableHead>
                <TableHead className="text-right">Work</TableHead>
                <TableHead className="text-right">OIL</TableHead>
                {isAdmin && <TableHead className="text-right">Edit</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.records.length === 0 && (
                <TableRow><TableCell colSpan={isAdmin ? 9 : 8} className="text-center text-muted-foreground py-8">No records for this period.</TableCell></TableRow>
              )}
              {list.records.map((r) => (
                <TableRow key={r.id} data-testid={`att-row-${r.date}`}>
                  <TableCell className="font-medium">{fmtDate(r.date)}{r.edited && <span className="ml-1 text-[10px] text-warning">(edited)</span>}</TableCell>
                  <TableCell className="font-mono-num">{fmtTime(r.clock_in)}</TableCell>
                  <TableCell className="font-mono-num text-muted-foreground">{fmtTime(r.break1_start)} – {fmtTime(r.break1_end)}</TableCell>
                  <TableCell className="font-mono-num text-muted-foreground">{fmtTime(r.break2_start)} – {fmtTime(r.break2_end)}</TableCell>
                  <TableCell className="font-mono-num">{fmtTime(r.clock_out)}</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {r.clock_in_meta && <div className="flex items-center gap-1"><span className="text-[10px] text-muted-foreground w-6">In</span><ClockMeta meta={r.clock_in_meta} /></div>}
                      {r.clock_out_meta && <div className="flex items-center gap-1"><span className="text-[10px] text-muted-foreground w-6">Out</span><ClockMeta meta={r.clock_out_meta} /></div>}
                      {!r.clock_in_meta && !r.clock_out_meta && <span className="text-xs text-muted-foreground">—</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono-num font-semibold">{r.work_hours}h</TableCell>
                  <TableCell className="text-right font-mono-num text-success">{r.access_hours > 0 ? `${r.access_hours}h` : "—"}</TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditRec(r)} data-testid={`edit-att-${r.date}`}><Pencil className="h-4 w-4" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      {isMgr && (
        <SectionCard title={`Team Work Log — ${digest.date || "Today"}`} className="mt-6">
          <div className="grid sm:grid-cols-2 gap-4">
            {(digest.entries || []).length === 0 && <p className="col-span-full text-sm text-muted-foreground py-6 text-center">No clock-outs logged yet today.</p>}
            {(digest.entries || []).map((e) => (
              <Card key={e.employee_id} className="p-4 rounded-md border-border" data-testid={`digest-${e.employee_id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <EmpAvatar emp={{ full_name: e.employee_name, photo_file_id: e.photo_file_id }} size="h-9 w-9" />
                    <p className="font-heading font-bold truncate">{e.employee_name}</p>
                  </div>
                  <span className="font-mono-num text-sm text-muted-foreground shrink-0">{e.work_hours}h · out {fmtTime(e.clock_out)}</span>
                </div>
                {e.work_log?.summary && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{e.work_log.summary}</p>}
                {(e.work_log?.items || []).length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {e.work_log.items.map((it, i) => (
                      <li key={i} className="text-sm flex items-center gap-2">
                        <span className={`h-1.5 w-1.5 rounded-full ${it.completed ? "bg-success" : "bg-warning"}`} />
                        <span className={it.completed ? "line-through text-muted-foreground" : ""}>{it.text}</span>
                        <span className={`text-xs ml-auto ${it.completed ? "text-success" : "text-warning"}`}>{it.completed ? "Completed" : "In progress"}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {!e.work_log && <p className="text-xs text-muted-foreground italic mt-2">Clocked out without a work log.</p>}
              </Card>
            ))}
          </div>
        </SectionCard>
      )}

      {clockOut && <ClockOutModal onClose={() => setClockOut(false)} onDone={() => { setClockOut(false); loadToday(); loadList(); loadDigest(); }} />}
      {editRec && <EditModal rec={editRec} onClose={() => setEditRec(null)} onDone={() => { setEditRec(null); loadList(); loadDigest(); }} />}
    </div>
  );
}

function ClockOutModal({ onClose, onDone }) {
  const [summary, setSummary] = useState("");
  const [items, setItems] = useState([{ text: "", completed: false }]);
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);

  const addItem = () => setItems([...items, { text: "", completed: false }]);
  const setItem = (i, patch) => setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const delItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const submit = async () => {
    setBusy(true);
    try {
      const ctx = await collectClockContext();
      await api.post("/attendance/clock-out", {
        summary,
        items: items.filter((i) => i.text.trim()),
        attachment_file_ids: attachments.map((a) => a.id),
        ...ctx,
      });
      toast.success("Clocked out — daily log saved");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-popover max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Clock Out — Daily Work Log</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Summarise what you worked on today and mark which items are completed. Example below is just a guide.</p>
        <div className="space-y-4 py-1">
          <div>
            <Label>Summary of the day</Label>
            <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} className="mt-1.5"
              placeholder="e.g. Closed 3 support tickets, drafted the Q3 sales deck, and reviewed the vendor contract with Finance." data-testid="worklog-summary" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label>Work items</Label>
              <Button size="sm" variant="ghost" onClick={addItem} data-testid="worklog-add-item"><Plus className="h-4 w-4 mr-1" /> Add item</Button>
            </div>
            {items.length === 0 && <p className="text-xs text-muted-foreground italic">e.g. "Finalise onboarding checklist" — Completed</p>}
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Checkbox checked={it.completed} onCheckedChange={(v) => setItem(i, { completed: !!v })} data-testid={`worklog-done-${i}`} />
                  <Input value={it.text} onChange={(e) => setItem(i, { text: e.target.value })} placeholder={i === 0 ? "e.g. Finalise onboarding checklist" : "Describe a task…"} className="h-9" data-testid={`worklog-item-${i}`} />
                  <span className={`text-xs w-20 ${it.completed ? "text-success" : "text-muted-foreground"}`}>{it.completed ? "Completed" : "In progress"}</span>
                  {items.length > 1 && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => delItem(i)}><Trash2 className="h-4 w-4" /></Button>}
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label>Attachments</Label>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <UploadButton label="Add file" onUploaded={(f) => setAttachments([...attachments, f])} />
              {attachments.map((a) => (
                <span key={a.id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border bg-muted"><Paperclip className="h-3 w-3" /> {a.filename || "file"}</span>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="worklog-submit"><LogOut className="h-4 w-4 mr-1.5" /> {busy ? "Saving…" : "Clock Out & Save Log"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditModal({ rec, onClose, onDone }) {
  const FIELDS = [
    ["clock_in", "Clock In"],
    ["break1_start", "Break 1 Out"],
    ["break1_end", "Break 1 In"],
    ["break2_start", "Break 2 Out"],
    ["break2_end", "Break 2 In"],
    ["clock_out", "Clock Out"],
  ];
  const [vals, setVals] = useState(() => Object.fromEntries(FIELDS.map(([f]) => [f, toSgtInput(rec[f])])));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!reason.trim()) { toast.error("Please enter an edit reason"); return; }
    setBusy(true);
    try {
      await api.put(`/attendance/${rec.id}/edit`, { ...vals, edit_reason: reason });
      toast.success("Attendance updated");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-popover max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Edit Attendance · {fmtDate(rec.date)}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Times are in Singapore Time. For overnight shifts, set Clock Out to the next day's date. Clear a field to remove it.</p>
        <div className="grid grid-cols-2 gap-3 py-2">
          {FIELDS.map(([f, label]) => (
            <div key={f}>
              <Label className="text-xs">{label}</Label>
              <Input type="datetime-local" value={vals[f]} onChange={(e) => setVals({ ...vals, [f]: e.target.value })} className="mt-1" data-testid={`edit-${f}`} />
            </div>
          ))}
        </div>
        <div>
          <Label className="text-xs">Edit Reason</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Staff forgot to clock out" className="mt-1" data-testid="edit-reason" />
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} data-testid="edit-save">{busy ? "Saving…" : "Save Changes"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
